import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi, tenantsApi, sensorsApi } from '../services/api';
import {
  UsersIcon,
  ServerStackIcon,
  ClipboardDocumentListIcon,
  BuildingOffice2Icon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  SignalIcon,
  PauseCircleIcon,
  PlayCircleIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  KeyIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { SkeletonRow } from '../components/common/Skeleton';
import ConfirmModal from '../components/common/ConfirmModal';

// ============================================================================
// Tab definitions
// ============================================================================

const tabs = [
  { id: 'tenants', label: 'Tenants', icon: BuildingOffice2Icon },
  { id: 'users', label: 'Users', icon: UsersIcon },
  { id: 'system', label: 'System Health', icon: ServerStackIcon },
  { id: 'audit', label: 'Audit Log', icon: ClipboardDocumentListIcon },
] as const;

type TabId = (typeof tabs)[number]['id'];

// ============================================================================
// Role badges
// ============================================================================

const roleBadge: Record<string, { bg: string; text: string }> = {
  ADMIN: { bg: 'bg-alert/10', text: 'text-alert' },
  OPERATOR: { bg: 'bg-accent/10', text: 'text-accent' },
  ANALYST: { bg: 'bg-sky/10', text: 'text-sky' },
  VIEWER: { bg: 'bg-gray-500/10', text: 'text-gray-400' },
  PILOT: { bg: 'bg-yellow-400/10', text: 'text-yellow-400' },
};

// ============================================================================
// Tier & Status badges
// ============================================================================

const tierBadge: Record<string, { bg: string; text: string }> = {
  FREE: { bg: 'bg-gray-500/10', text: 'text-gray-400' },
  STARTER: { bg: 'bg-sky/10', text: 'text-sky' },
  PROFESSIONAL: { bg: 'bg-accent/10', text: 'text-accent' },
  ENTERPRISE: { bg: 'bg-yellow-400/10', text: 'text-yellow-400' },
};

const statusBadge: Record<string, { bg: string; text: string; dot: string }> = {
  ACTIVE: { bg: 'bg-accent/10', text: 'text-accent', dot: 'bg-accent' },
  TRIALING: { bg: 'bg-sky/10', text: 'text-sky', dot: 'bg-sky' },
  PAST_DUE: { bg: 'bg-yellow-400/10', text: 'text-yellow-400', dot: 'bg-yellow-400' },
  CANCELLED: { bg: 'bg-alert/10', text: 'text-alert', dot: 'bg-alert' },
};

// ============================================================================
// Tenants Tab (SUPER_ADMIN)
// ============================================================================

function TenantsTab() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showProvision, setShowProvision] = useState(false);
  const [suspendTarget, setSuspendTarget] = useState<string | null>(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [provisionResult, setProvisionResult] = useState<any>(null);

  const [form, setForm] = useState({
    name: '', slug: '', tier: 'PROFESSIONAL', domain: '',
    adminEmail: '', adminFirst: '', adminLast: '', adminPassword: '',
    timezone: 'UTC', unitSystem: 'METRIC',
  });

  const { data: tenantsData, isLoading } = useQuery({
    queryKey: ['admin-tenants', search, statusFilter, tierFilter, page],
    queryFn: () => tenantsApi.list({
      search: search || undefined,
      status: statusFilter || undefined,
      tier: tierFilter || undefined,
      page,
      pageSize: 10,
    }),
  });

  const provisionMutation = useMutation({
    mutationFn: () => tenantsApi.provision({
      tenant: {
        name: form.name,
        slug: form.slug,
        domain: form.domain || undefined,
        subscriptionTier: form.tier,
        settings: { timezone: form.timezone, unitSystem: form.unitSystem },
      },
      adminUser: {
        email: form.adminEmail,
        firstName: form.adminFirst,
        lastName: form.adminLast,
        password: form.adminPassword,
      },
    }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
      toast.success('Tenant provisioned successfully');
      setProvisionResult(data.data);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (err: any) => toast.error(err?.response?.data?.error?.message || 'Provisioning failed'),
  });

  const suspendMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => tenantsApi.suspend(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
      toast.success('Tenant suspended');
      setSuspendTarget(null);
      setSuspendReason('');
    },
    onError: () => toast.error('Failed to suspend tenant'),
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => tenantsApi.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
      toast.success('Tenant activated');
    },
    onError: () => toast.error('Failed to activate tenant'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => tenantsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenants'] });
      toast.success('Tenant removed');
    },
    onError: () => toast.error('Failed to remove tenant'),
  });

  const tenants = tenantsData?.data?.tenants || [];
  const total = tenantsData?.data?.total || 0;
  const totalPages = Math.ceil(total / 10);

  const resetForm = () => {
    setForm({ name: '', slug: '', tier: 'PROFESSIONAL', domain: '', adminEmail: '', adminFirst: '', adminLast: '', adminPassword: '', timezone: 'UTC', unitSystem: 'METRIC' });
    setProvisionResult(null);
    setShowProvision(false);
  };

  // Auto-generate slug from name
  const handleNameChange = (name: string) => {
    setForm((f) => ({
      ...f,
      name,
      slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''),
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header with search & filters */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-white">Tenant Management</h3>
            <p className="text-sm text-gray-500">{total} registered tenants</p>
          </div>
          <button onClick={() => setShowProvision(true)} className="btn-primary flex items-center gap-2 text-sm">
            <PlusIcon className="h-4 w-4" />Provision Tenant
          </button>
        </div>

        {/* Search & Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search tenants..."
              className="w-full pl-10 pr-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
          </div>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 appearance-none cursor-pointer">
            <option value="">All Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="TRIALING">Trialing</option>
            <option value="PAST_DUE">Past Due</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
          <select value={tierFilter} onChange={(e) => { setTierFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 appearance-none cursor-pointer">
            <option value="">All Tiers</option>
            <option value="FREE">Free</option>
            <option value="STARTER">Starter</option>
            <option value="PROFESSIONAL">Professional</option>
            <option value="ENTERPRISE">Enterprise</option>
          </select>
        </div>

        {/* Tenant Table */}
        {isLoading ? (
          <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-navy-700">
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 px-3">Tenant</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 px-3">Tier</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 px-3">Status</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 px-3">Users</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 px-3">Sensors</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 px-3">Created</th>
                  <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 px-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-700/50">
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {tenants.map((tenant: any) => {
                  const tier = tierBadge[tenant.subscription_tier] || tierBadge.FREE;
                  const status = statusBadge[tenant.subscription_status] || statusBadge.ACTIVE;
                  const isSuspended = tenant.subscription_status === 'CANCELLED';
                  return (
                    <tr key={tenant.id} className="hover:bg-navy-800/50 transition-colors">
                      <td className="py-3 px-3">
                        <div>
                          <p className="text-sm font-medium text-white">{tenant.name}</p>
                          <p className="text-xs text-gray-500">{tenant.slug}{tenant.domain ? ` · ${tenant.domain}` : ''}</p>
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <span className={clsx('text-[11px] px-2.5 py-1 rounded-full font-semibold', tier.bg, tier.text)}>
                          {tenant.subscription_tier}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <span className={clsx('inline-flex items-center gap-1.5 text-xs font-medium', status.text)}>
                          <div className={clsx('w-1.5 h-1.5 rounded-full', status.dot)} />
                          {tenant.subscription_status}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-sm text-gray-400">{tenant.user_count || 0}</td>
                      <td className="py-3 px-3 text-sm text-gray-400">{tenant.sensor_count || 0}</td>
                      <td className="py-3 px-3">
                        <span className="text-xs text-gray-500">
                          {tenant.created_at ? new Date(tenant.created_at).toLocaleDateString() : '—'}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center justify-end gap-1">
                          {isSuspended ? (
                            <button onClick={() => activateMutation.mutate(tenant.id)}
                              title="Activate"
                              className="p-1.5 text-accent hover:text-white transition-colors rounded-lg hover:bg-accent/10">
                              <PlayCircleIcon className="h-4 w-4" />
                            </button>
                          ) : (
                            <button onClick={() => setSuspendTarget(tenant.id)}
                              title="Suspend"
                              className="p-1.5 text-yellow-400 hover:text-white transition-colors rounded-lg hover:bg-yellow-400/10">
                              <PauseCircleIcon className="h-4 w-4" />
                            </button>
                          )}
                          <button className="p-1.5 text-gray-500 hover:text-white transition-colors rounded-lg hover:bg-navy-700">
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button onClick={() => setDeleteTarget(tenant.id)}
                            className="p-1.5 text-gray-500 hover:text-alert transition-colors rounded-lg hover:bg-alert/10">
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {tenants.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-10 text-center">
                      <BuildingOffice2Icon className="h-10 w-10 text-gray-600 mx-auto mb-3" />
                      <p className="text-sm text-gray-400">No tenants found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-navy-700">
            <p className="text-xs text-gray-500">Page {page} of {totalPages} ({total} tenants)</p>
            <div className="flex gap-1">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30 transition-colors rounded-lg hover:bg-navy-700">
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="p-1.5 text-gray-500 hover:text-white disabled:opacity-30 transition-colors rounded-lg hover:bg-navy-700">
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Provision Modal */}
      {showProvision && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="glass-card p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
            {!provisionResult ? (
              <>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-white">Provision New Tenant</h3>
                    <p className="text-sm text-gray-500">Creates tenant, admin user, and API key in one step</p>
                  </div>
                  <button onClick={resetForm} className="text-gray-500 hover:text-white">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Tenant Details */}
                <div className="mb-5">
                  <h4 className="text-xs font-semibold text-accent uppercase tracking-wider mb-3">Organization</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">Name *</label>
                      <input value={form.name} onChange={(e) => handleNameChange(e.target.value)}
                        placeholder="ACME Defense Corp"
                        className="w-full px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">Slug *</label>
                      <input value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                        placeholder="acme-defense"
                        className="w-full px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">Subscription Tier *</label>
                      <select value={form.tier} onChange={(e) => setForm((f) => ({ ...f, tier: e.target.value }))}
                        className="w-full px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 appearance-none cursor-pointer">
                        <option value="FREE">Free</option>
                        <option value="STARTER">Starter</option>
                        <option value="PROFESSIONAL">Professional</option>
                        <option value="ENTERPRISE">Enterprise</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">Custom Domain</label>
                      <input value={form.domain} onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))}
                        placeholder="asne.acme.com"
                        className="w-full px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
                    </div>
                  </div>
                </div>

                {/* Admin User */}
                <div className="mb-5">
                  <h4 className="text-xs font-semibold text-accent uppercase tracking-wider mb-3">Admin User</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">First Name *</label>
                      <input value={form.adminFirst} onChange={(e) => setForm((f) => ({ ...f, adminFirst: e.target.value }))}
                        className="w-full px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">Last Name *</label>
                      <input value={form.adminLast} onChange={(e) => setForm((f) => ({ ...f, adminLast: e.target.value }))}
                        className="w-full px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">Email *</label>
                      <input type="email" value={form.adminEmail} onChange={(e) => setForm((f) => ({ ...f, adminEmail: e.target.value }))}
                        placeholder="admin@acme-defense.com"
                        className="w-full px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">Temporary Password *</label>
                      <input type="password" value={form.adminPassword} onChange={(e) => setForm((f) => ({ ...f, adminPassword: e.target.value }))}
                        placeholder="Min 8 characters"
                        className="w-full px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
                    </div>
                  </div>
                </div>

                {/* Settings */}
                <div className="mb-6">
                  <h4 className="text-xs font-semibold text-accent uppercase tracking-wider mb-3">Settings</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">Timezone</label>
                      <input value={form.timezone} onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
                        className="w-full px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">Unit System</label>
                      <select value={form.unitSystem} onChange={(e) => setForm((f) => ({ ...f, unitSystem: e.target.value }))}
                        className="w-full px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 appearance-none cursor-pointer">
                        <option value="METRIC">Metric</option>
                        <option value="IMPERIAL">Imperial</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <button onClick={resetForm} className="btn-secondary px-4 py-2 text-sm">Cancel</button>
                  <button onClick={() => provisionMutation.mutate()}
                    disabled={!form.name || !form.slug || !form.adminEmail || !form.adminPassword || !form.adminFirst || provisionMutation.isPending}
                    className="btn-primary px-4 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed">
                    {provisionMutation.isPending ? 'Provisioning...' : 'Provision Tenant'}
                  </button>
                </div>
              </>
            ) : (
              /* Provisioning Result */
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-full bg-accent/10">
                    <CheckCircleIcon className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Tenant Provisioned</h3>
                    <p className="text-sm text-gray-500">Save the API key below — it will not be shown again</p>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="p-4 bg-navy-800 rounded-lg border border-navy-700">
                    <h4 className="text-xs font-semibold text-accent uppercase tracking-wider mb-2">Tenant</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-gray-400">ID:</span>
                      <span className="text-white font-mono text-xs">{provisionResult.tenant.id}</span>
                      <span className="text-gray-400">Name:</span>
                      <span className="text-white">{provisionResult.tenant.name}</span>
                      <span className="text-gray-400">Tier:</span>
                      <span className="text-white">{provisionResult.tenant.subscriptionTier}</span>
                    </div>
                  </div>

                  <div className="p-4 bg-navy-800 rounded-lg border border-navy-700">
                    <h4 className="text-xs font-semibold text-accent uppercase tracking-wider mb-2">Admin User</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-gray-400">Email:</span>
                      <span className="text-white">{provisionResult.adminUser.email}</span>
                      <span className="text-gray-400">Name:</span>
                      <span className="text-white">{provisionResult.adminUser.firstName} {provisionResult.adminUser.lastName}</span>
                    </div>
                  </div>

                  <div className="p-4 bg-navy-800 rounded-lg border border-accent/30 glow-border">
                    <div className="flex items-center gap-2 mb-2">
                      <KeyIcon className="h-4 w-4 text-accent" />
                      <h4 className="text-xs font-semibold text-accent uppercase tracking-wider">API Key (copy now!)</h4>
                    </div>
                    <div className="p-3 bg-navy-900 rounded-md border border-navy-700">
                      <code className="text-xs text-accent font-mono break-all select-all">
                        {provisionResult.apiKey.fullKey}
                      </code>
                    </div>
                  </div>

                  {/* Onboarding checklist */}
                  <div className="p-4 bg-navy-800 rounded-lg border border-navy-700">
                    <div className="flex items-center gap-2 mb-3">
                      <ClipboardDocumentCheckIcon className="h-4 w-4 text-sky" />
                      <h4 className="text-xs font-semibold text-sky uppercase tracking-wider">Onboarding Checklist</h4>
                    </div>
                    <div className="space-y-2">
                      {[
                        'Share admin credentials securely with customer',
                        'Customer logs in and changes password',
                        'Customer enables MFA',
                        'Register sensors and ingestion endpoints',
                        'Configure geofences for protected areas',
                        'Validate data flow from sensors',
                        'Create additional users per org chart',
                        'Conduct acceptance testing',
                        'Go live — enable production feeds',
                      ].map((item, i) => (
                        <label key={i} className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer hover:text-gray-300">
                          <input type="checkbox" className="rounded border-navy-600 text-accent focus:ring-accent/30 bg-navy-900" />
                          {item}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button onClick={resetForm} className="btn-primary px-4 py-2 text-sm">Done</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Suspend Modal */}
      {suspendTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="glass-card p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-white mb-2">Suspend Tenant</h3>
            <p className="text-sm text-gray-400 mb-4">This will deactivate all API keys and block access. The tenant can be reactivated later.</p>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-400 mb-1">Reason *</label>
              <textarea value={suspendReason} onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="e.g. Non-payment, policy violation, customer request..."
                rows={3}
                className="w-full px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent resize-none" />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => { setSuspendTarget(null); setSuspendReason(''); }} className="btn-secondary px-4 py-2 text-sm">Cancel</button>
              <button onClick={() => suspendMutation.mutate({ id: suspendTarget, reason: suspendReason })}
                disabled={!suspendReason || suspendMutation.isPending}
                className="px-4 py-2 text-sm font-medium bg-yellow-400/10 text-yellow-400 border border-yellow-400/30 rounded-lg hover:bg-yellow-400/20 transition-colors disabled:opacity-40">
                {suspendMutation.isPending ? 'Suspending...' : 'Suspend'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmModal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        onConfirm={() => { if (deleteTarget) deleteMutation.mutate(deleteTarget); }}
        title="Remove Tenant" message="Are you sure you want to remove this tenant? All data, users, and API keys will be deactivated. This action cannot be easily undone."
        confirmLabel="Remove" variant="danger" />
    </div>
  );
}

// ============================================================================
// Users Tab
// ============================================================================

function UsersTab() {
  const queryClient = useQueryClient();
  const [showInvite, setShowInvite] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '', role: 'VIEWER', password: '' });

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => usersApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: () => usersApi.create({ ...form }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('User created');
      setShowInvite(false);
      setForm({ email: '', firstName: '', lastName: '', role: 'VIEWER', password: '' });
    },
    onError: () => toast.error('Failed to create user'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => usersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('User removed');
    },
    onError: () => toast.error('Failed to remove user'),
  });

  const users = usersData?.data || [];

  return (
    <div className="space-y-6">
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-white">User Management</h3>
            <p className="text-sm text-gray-500">{users.length} registered users</p>
          </div>
          <button onClick={() => setShowInvite(true)} className="btn-primary flex items-center gap-2 text-sm">
            <PlusIcon className="h-4 w-4" />Invite User
          </button>
        </div>

        {/* Invite form */}
        {showInvite && (
          <div className="mb-6 p-5 bg-navy-800 border border-navy-700 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-semibold text-white">Invite New User</h4>
              <button onClick={() => setShowInvite(false)} className="text-gray-500 hover:text-white">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">First Name</label>
                <input value={form.firstName} onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                  className="w-full px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Last Name</label>
                <input value={form.lastName} onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                  className="w-full px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">Role</label>
                <select value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                  className="w-full px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent appearance-none cursor-pointer">
                  <option value="VIEWER">Viewer</option>
                  <option value="ANALYST">Analyst</option>
                  <option value="OPERATOR">Operator</option>
                  <option value="PILOT">Pilot</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-400 mb-1">Temporary Password</label>
                <input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder="User will be prompted to change on first login"
                  className="w-full px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent" />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowInvite(false)} className="btn-secondary px-4 py-2 text-sm">Cancel</button>
              <button onClick={() => createMutation.mutate()}
                disabled={!form.email || !form.firstName || !form.password || createMutation.isPending}
                className="btn-primary px-4 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed">
                {createMutation.isPending ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        )}

        {/* Users table */}
        {isLoading ? (
          <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-navy-700">
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 px-3">User</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 px-3">Role</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 px-3">Status</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 px-3">Last Active</th>
                  <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 px-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-700/50">
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {users.map((user: any) => {
                  const badge = roleBadge[user.role] || roleBadge.VIEWER;
                  return (
                    <tr key={user.id} className="hover:bg-navy-800/50 transition-colors">
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center text-xs font-semibold text-accent">
                            {(user.firstName?.[0] || '').toUpperCase()}{(user.lastName?.[0] || '').toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white">{user.firstName} {user.lastName}</p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <span className={clsx('text-[11px] px-2.5 py-1 rounded-full font-semibold', badge.bg, badge.text)}>
                          {user.role}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <span className={clsx('inline-flex items-center gap-1.5 text-xs',
                          user.isActive !== false ? 'text-accent' : 'text-gray-500')}>
                          <div className={clsx('w-1.5 h-1.5 rounded-full', user.isActive !== false ? 'bg-accent' : 'bg-gray-500')} />
                          {user.isActive !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <span className="text-xs text-gray-500">
                          {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center justify-end gap-1">
                          <button className="p-1.5 text-gray-500 hover:text-white transition-colors rounded-lg hover:bg-navy-700">
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button onClick={() => setDeleteTarget(user.id)}
                            className="p-1.5 text-gray-500 hover:text-alert transition-colors rounded-lg hover:bg-alert/10">
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        onConfirm={() => { if (deleteTarget) deleteMutation.mutate(deleteTarget); }}
        title="Remove User" message="Are you sure you want to remove this user? They will lose access to the platform immediately."
        confirmLabel="Remove" variant="danger" />
    </div>
  );
}

// ============================================================================
// System Health Tab
// ============================================================================

const systemServices = [
  { name: 'API Server', status: 'operational', latency: '12ms', uptime: '99.98%', icon: ServerStackIcon },
  { name: 'Database (PostgreSQL)', status: 'operational', latency: '3ms', uptime: '99.99%', icon: ServerStackIcon },
  { name: 'Ingestion Pipeline', status: 'operational', latency: '45ms', uptime: '99.95%', icon: SignalIcon },
  { name: 'Redis Cache', status: 'operational', latency: '1ms', uptime: '99.99%', icon: ServerStackIcon },
  { name: 'WebSocket Gateway', status: 'degraded', latency: '120ms', uptime: '98.50%', icon: SignalIcon },
  { name: 'Analytics Engine', status: 'operational', latency: '200ms', uptime: '99.90%', icon: ServerStackIcon },
];

const statusStyles: Record<string, { color: string; bg: string; label: string; icon: typeof CheckCircleIcon }> = {
  operational: { color: 'text-accent', bg: 'bg-accent/10', label: 'Operational', icon: CheckCircleIcon },
  degraded: { color: 'text-yellow-400', bg: 'bg-yellow-400/10', label: 'Degraded', icon: ExclamationTriangleIcon },
  down: { color: 'text-alert', bg: 'bg-alert/10', label: 'Down', icon: ExclamationTriangleIcon },
};

function SystemHealthTab() {
  // Live sensor-health snapshot — previously lived on the Dashboard's
  // "System Status" card; consolidated here so all health data is in one
  // place and the dashboard can stay focused on surveillance visuals.
  const { data: sensorsData } = useQuery({
    queryKey: ['sensors'],
    queryFn: () => sensorsApi.list(),
  });
  const sensors = sensorsData?.data || [];
  const onlineSensors = sensors.filter((s: any) => s.status === 'ONLINE').length;
  const offlineSensors = sensors.length - onlineSensors;
  const allOk = offlineSensors === 0;

  return (
    <div className="space-y-6">
      {/* Overall status */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className={clsx('w-3 h-3 rounded-full animate-pulse', allOk ? 'bg-accent' : 'bg-alert')} />
          <h3 className="text-lg font-semibold text-white">
            {allOk ? 'All Systems Operational' : `${offlineSensors} sensor${offlineSensors === 1 ? '' : 's'} offline`}
          </h3>
        </div>
        <p className="text-sm text-gray-500">Last checked: {new Date().toLocaleTimeString()}</p>
      </div>

      {/* Live status summary (moved from Dashboard) — concise at-a-glance
          list of the four core subsystems. The Sensors row is live data;
          the others remain static status pills for now and will become
          live probes when Phase 2 introduces health endpoints. */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-display font-semibold text-white uppercase tracking-wider">Live Status</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { label: 'API Status', status: 'Operational', ok: true },
            { label: 'Database', status: 'Connected', ok: true },
            { label: 'Sensors', status: `${onlineSensors}/${sensors.length} Online`, ok: sensors.length === 0 ? true : onlineSensors > 0 },
            { label: 'Ingestion', status: 'Active', ok: true },
          ].map((item) => (
            <div key={item.label} className="flex items-center justify-between rounded-lg border border-navy-700 bg-navy-800/40 px-4 py-3">
              <div className="flex items-center gap-2.5">
                <div className={clsx('w-2 h-2 rounded-full', item.ok ? 'bg-accent animate-pulse-dot' : 'bg-alert')} />
                <span className="text-sm text-gray-400">{item.label}</span>
              </div>
              <span className={clsx('text-sm font-medium', item.ok ? 'text-accent' : 'text-alert')}>{item.status}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Service cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {systemServices.map((service) => {
          const style = statusStyles[service.status] || statusStyles.operational;
          const StatusIcon = style.icon;
          return (
            <div key={service.name} className="glass-card p-4 glow-border">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={clsx('p-2 rounded-lg', style.bg, style.color)}>
                    <service.icon className="h-5 w-5" />
                  </div>
                  <h4 className="text-sm font-medium text-white">{service.name}</h4>
                </div>
                <span className={clsx('inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full font-semibold', style.bg, style.color)}>
                  <StatusIcon className="h-3 w-3" />
                  {style.label}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[11px] text-gray-500">Latency</p>
                  <p className="text-sm font-semibold text-white">{service.latency}</p>
                </div>
                <div>
                  <p className="text-[11px] text-gray-500">Uptime</p>
                  <p className="text-sm font-semibold text-white">{service.uptime}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Throughput stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'API Requests/min', value: '2,847', color: 'text-white' },
          { label: 'Tracks Ingested/min', value: '1,203', color: 'text-accent' },
          { label: 'WS Connections', value: '48', color: 'text-sky' },
          { label: 'Queue Depth', value: '12', color: 'text-yellow-400' },
        ].map((s) => (
          <div key={s.label} className="glass-card p-4">
            <p className="text-xs text-gray-500">{s.label}</p>
            <p className={`text-2xl font-display font-bold ${s.color} mt-1`}>{s.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Audit Log Tab
// ============================================================================

const sampleAuditLog = [
  { id: '1', timestamp: '2026-02-08T14:32:00Z', user: 'John Doe', action: 'LOGIN', resource: 'Auth', details: 'Successful login from 192.168.1.10', severity: 'INFO' },
  { id: '2', timestamp: '2026-02-08T14:28:00Z', user: 'System', action: 'ALERT_TRIGGERED', resource: 'Geofence', details: 'Track TRK-4821 entered exclusion zone "Airport Perimeter"', severity: 'CRITICAL' },
  { id: '3', timestamp: '2026-02-08T14:15:00Z', user: 'Jane Smith', action: 'CREATE', resource: 'Mission', details: 'Created mission "Survey Route Alpha"', severity: 'INFO' },
  { id: '4', timestamp: '2026-02-08T13:52:00Z', user: 'System', action: 'SENSOR_OFFLINE', resource: 'Sensor', details: 'Radar unit RDR-003 went offline', severity: 'WARNING' },
  { id: '5', timestamp: '2026-02-08T13:30:00Z', user: 'Admin', action: 'UPDATE', resource: 'User', details: 'Updated role for user mike@asne.io to OPERATOR', severity: 'INFO' },
  { id: '6', timestamp: '2026-02-08T12:45:00Z', user: 'System', action: 'TRACK_CLASSIFIED', resource: 'Track', details: 'Track TRK-4819 classified as DRONE (confidence: 94%)', severity: 'INFO' },
  { id: '7', timestamp: '2026-02-08T12:10:00Z', user: 'John Doe', action: 'DELETE', resource: 'API Key', details: 'Revoked API key "Test Integration"', severity: 'WARNING' },
  { id: '8', timestamp: '2026-02-08T11:55:00Z', user: 'System', action: 'INGESTION_ERROR', resource: 'Ingestion', details: 'Failed to parse ADS-B message: malformed ICAO address', severity: 'WARNING' },
];

const severityStyle: Record<string, { dot: string; text: string }> = {
  INFO: { dot: 'bg-sky', text: 'text-sky' },
  WARNING: { dot: 'bg-yellow-400', text: 'text-yellow-400' },
  CRITICAL: { dot: 'bg-alert', text: 'text-alert' },
};

function AuditLogTab() {
  const [filter, setFilter] = useState<'ALL' | 'INFO' | 'WARNING' | 'CRITICAL'>('ALL');

  const filteredLogs = filter === 'ALL' ? sampleAuditLog : sampleAuditLog.filter((l) => l.severity === filter);

  return (
    <div className="glass-card p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white">Audit Log</h3>
          <p className="text-sm text-gray-500">System and user activity history</p>
        </div>
        <div className="flex gap-1 bg-navy-800 rounded-lg p-1 border border-navy-700">
          {(['ALL', 'INFO', 'WARNING', 'CRITICAL'] as const).map((level) => (
            <button key={level} onClick={() => setFilter(level)}
              className={clsx('px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                filter === level ? 'bg-accent text-navy-900' : 'text-gray-400 hover:text-white')}>
              {level === 'ALL' ? 'All' : level.charAt(0) + level.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {filteredLogs.map((entry) => {
          const style = severityStyle[entry.severity] || severityStyle.INFO;
          return (
            <div key={entry.id} className="flex items-start gap-4 p-3 rounded-lg hover:bg-navy-800/50 transition-colors">
              <div className={clsx('w-2 h-2 rounded-full mt-1.5 flex-shrink-0', style.dot)} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-0.5">
                  <span className="text-sm font-medium text-white">{entry.action.replace(/_/g, ' ')}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-navy-700 text-gray-400">{entry.resource}</span>
                  <span className={clsx('text-[10px] font-semibold', style.text)}>{entry.severity}</span>
                </div>
                <p className="text-xs text-gray-400">{entry.details}</p>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-[11px] text-gray-600">{entry.user}</span>
                  <span className="text-[11px] text-gray-600">{new Date(entry.timestamp).toLocaleString()}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredLogs.length === 0 && (
        <div className="text-center py-10">
          <ClipboardDocumentListIcon className="h-10 w-10 text-gray-600 mx-auto mb-3" />
          <p className="text-sm text-gray-400">No audit entries match this filter.</p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Admin Page
// ============================================================================

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState<TabId>('tenants');

  return (
    <div className="min-h-screen bg-navy-900 p-6 grid-overlay">
      <div className="mb-6">
        <h1 className="page-title">Administration</h1>
        <p className="mt-1 text-sm text-gray-500">Manage tenants, users, monitor system health, and review audit logs</p>
      </div>

      {/* Tab nav */}
      <div className="mb-6 flex gap-1 bg-navy-800 rounded-lg p-1 border border-navy-700 w-fit">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={clsx('flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
                activeTab === tab.id ? 'bg-accent text-navy-900' : 'text-gray-400 hover:text-white')}>
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'tenants' && <TenantsTab />}
      {activeTab === 'users' && <UsersTab />}
      {activeTab === 'system' && <SystemHealthTab />}
      {activeTab === 'audit' && <AuditLogTab />}
    </div>
  );
}
